import { useCallback, useEffect, useRef, useState } from 'react';
import { bearingOf } from '@playnets/sim';
import { useUi } from '../store';
import type { GameHandle } from '../game/useGame';

/**
 * Four things, and nothing else:
 *
 *   juice count    top-left
 *   wave dots      top-centre
 *   pause          top-right
 *   ultimate       bottom-right, and only during a wave
 *
 * Core Integrity isn't here — it's drawn as an arc on the planet, where it
 * belongs. Nor is the enemy count, the wave number as text, or the loose-juice
 * tally: all of that is already visible on the field. Every readout that
 * duplicates something you can see is a readout that earns nothing.
 */
export default function Hud({ game }: { game: GameHandle | null }): JSX.Element {
  const hud = useUi((s) => s.hud);
  const paused = useUi((s) => s.paused);

  const [aim, setAim] = useState<number | null>(null);
  /**
   * The juice counter reacts when it changes: a kick up when you're paid, a
   * flinch down when you spend. Without it the one number on the screen was
   * the only thing that never acknowledged you'd done anything.
   */
  const prevJuice = useRef(hud.juice);
  const [tick, setTick] = useState<'up' | 'down' | null>(null);
  useEffect(() => {
    if (hud.juice === prevJuice.current) return;
    const dir = hud.juice > prevJuice.current ? 'up' : 'down';
    prevJuice.current = hud.juice;
    setTick(dir);
    const t = window.setTimeout(() => setTick(null), 260);
    return () => window.clearTimeout(t);
  }, [hud.juice]);

  const ready = hud.ultCd <= 0;
  const ultName = game?.world.ultimate.name ?? 'Ultimate';
  const aimed = game?.world.ultimate.aimed ?? false;

  /**
   * Screen point → bearing from the planet. Goes through the renderer's own
   * transform rather than re-deriving it: the canvas is full-bleed and centred
   * now, so "plate top is world y=0" stopped being true.
   */
  const bearingAt = useCallback(
    (clientX: number, clientY: number): number => {
      if (!game) return -Math.PI / 2;
      const p = game.renderer.toLogical(clientX, clientY);
      return bearingOf(p.x, p.y);
    },
    [game],
  );

  const fire = useCallback(
    (bearing: number) => {
      game?.input.push({ kind: 'ultimate', bearing });
      useUi.getState().markCoached('ultimate');
    },
    [game],
  );

  /* Aim by dragging inward from the ult button and releasing (§9). */
  const onUltDown = (ev: React.PointerEvent): void => {
    if (!ready) return;
    ev.currentTarget.setPointerCapture(ev.pointerId);
    if (aimed) setAim(bearingAt(ev.clientX, ev.clientY));
  };
  const onUltMove = (ev: React.PointerEvent): void => {
    if (!aimed || aim === null) return;
    setAim(bearingAt(ev.clientX, ev.clientY));
  };
  const onUltUp = (ev: React.PointerEvent): void => {
    if (!ready) return;
    if (aimed) {
      fire(aim ?? bearingAt(ev.clientX, ev.clientY));
      setAim(null);
    } else {
      fire(0);
    }
  };

  const charge = hud.ultTotal > 0 ? 1 - hud.ultCd / hud.ultTotal : 1;

  return (
    <div className="hud">
      {/* A soft darkening at the very top and bottom. The HUD has to stay
          readable over a bright planet or a wall of enemies, and this is
          cheaper than outlining every glyph. */}
      <div className="hud-scrim" />

      <div className={`juice${tick ? ` is-${tick}` : ''}`}>
        <span className="juice-blob" />
        <span className="juice-count">{hud.juice}</span>
      </div>

      {/* Wave progress as dots. Filled = cleared, ringed = the one you're on. */}
      <div className="dots" aria-label={`Wave ${hud.wave} of ${hud.waveCount}`}>
        {Array.from({ length: hud.waveCount }, (_, i) => (
          <i
            key={i}
            className={
              i < hud.wave - 1 ? 'is-done' : i === hud.wave - 1 ? 'is-now' : undefined
            }
          />
        ))}
      </div>

      <button
        className="capsule capsule--tr"
        onClick={() => useUi.getState().setPaused(!paused)}
        aria-label={paused ? 'Resume' : 'Pause'}
      >
        {paused ? '▶' : '❙❙'}
      </button>

      {/* An ultimate is a wave-time decision by rule, so it isn't here during a
          build phase — no greyed-out button under the Start Wave CTA. */}
      {!hud.building && (
        <div className="capsule capsule--br">
          <button
            className={`ult${ready ? ' is-ready' : ''}`}
            onPointerDown={onUltDown}
            onPointerMove={onUltMove}
            onPointerUp={onUltUp}
            disabled={!ready}
            aria-label={ultName}
          >
            {/* Charge sweeps round the rim rather than filling the button
                like a beaker. A radial dial is how long cooldowns read
                everywhere else, and it leaves the face free for the name. */}
            <span
              className="ult-dial"
              style={{ ['--charge' as string]: `${Math.round(charge * 360)}deg` }}
            />
            <span className="ult-label">{ready ? ultName : ''}</span>
          </button>
        </div>
      )}

      {/* The aim cone. A square viewBox centred on the planet, so angles stay
          angles whatever shape the screen is. */}
      {aim !== null && (
        <svg className="ult-aim" viewBox="-1 -1 2 2" preserveAspectRatio="xMidYMid meet">
          <path
            d={coneWedge(aim, game?.world.ultimate.spread ?? Math.PI / 2)}
            fill="rgba(255,122,61,0.24)"
          />
        </svg>
      )}
    </div>
  );
}

/** In unit space: origin at the planet, radius 2 to overshoot any screen. */
function coneWedge(bearing: number, spread: number): string {
  const r = 2;
  const half = Math.min(spread, Math.PI * 1.9) * 0.5;
  const x1 = Math.cos(bearing - half) * r;
  const y1 = Math.sin(bearing - half) * r;
  const x2 = Math.cos(bearing + half) * r;
  const y2 = Math.sin(bearing + half) * r;
  const large = half > Math.PI / 2 ? 1 : 0;
  return `M 0 0 L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}
